/**
 * listing-merge-service.ts
 *
 * Non-Destructive 3-Way OCC Conflict Resolver and Scoped Ephemeral Draft Storage
 * for Sovereign Commerce Catalog Management.
 */

import type { Product, LocalizedContent } from '../commerce-contracts.js';

export interface FieldConflict {
  readonly fieldPath: string;
  readonly baseValue: unknown;
  readonly localValue: unknown;
  readonly remoteValue: unknown;
  readonly reason: string;
}

export interface MergeResult<T = Product> {
  readonly status: 'clean' | 'conflict';
  readonly merged?: T;
  readonly conflicts: readonly FieldConflict[];
  readonly baseRevision: number;
  readonly localRevision: number;
  readonly remoteRevision: number;
}

export interface ListingDraft {
  readonly draftId: string;
  readonly productId: string;
  readonly workspaceId: string;
  readonly authorId: string;
  readonly deviceId: string;
  readonly editorSessionId: string;
  readonly baseRevision: number;
  readonly contentHash: string;
  readonly draftSavedAt: string;
  readonly expiresAt: string;
  readonly content: Partial<Product>;
}

/**
 * Merges localized content maps on a key-by-key basis.
 */
function mergeLocalizedContent(
  base: LocalizedContent = {},
  local: LocalizedContent = {},
  remote: LocalizedContent = {}
): { merged: LocalizedContent; conflicts: FieldConflict[] } {
  const merged: LocalizedContent = {};
  const conflicts: FieldConflict[] = [];

  const allLocales = new Set([
    ...Object.keys(base),
    ...Object.keys(local),
    ...Object.keys(remote),
  ]);

  for (const loc of allLocales) {
    const baseVal = base[loc];
    const localVal = local[loc];
    const remoteVal = remote[loc];

    if (localVal === remoteVal) {
      if (localVal !== undefined) merged[loc] = localVal;
    } else if (baseVal === localVal) {
      // Local didn't change; accept remote
      if (remoteVal !== undefined) merged[loc] = remoteVal;
    } else if (baseVal === remoteVal) {
      // Remote didn't change; keep local
      if (localVal !== undefined) merged[loc] = localVal;
    } else {
      // Both changed differently
      conflicts.push({
        fieldPath: `title[${loc}]`,
        baseValue: baseVal,
        localValue: localVal,
        remoteValue: remoteVal,
        reason: `Conflicting localized text for locale "${loc}"`,
      });
    }
  }

  return { merged, conflicts };
}

/**
 * Computes a non-destructive 3-way merge between base, local, and remote product snapshots.
 */
export function computeThreeWayMerge(
  base: Product,
  local: Partial<Product>,
  remote: Product
): MergeResult<Product> {
  const conflicts: FieldConflict[] = [];
  const merged: any = { ...remote };

  // 1. Revision Check
  const baseRevision = base.revision;
  const localRevision = local.revision ?? base.revision;
  const remoteRevision = remote.revision;

  // 2. Title Merge
  const titleRes = mergeLocalizedContent(base.title, local.title, remote.title);
  if (titleRes.conflicts.length > 0) {
    conflicts.push(...titleRes.conflicts);
  } else {
    merged.title = titleRes.merged;
  }

  // 3. Description Merge
  if (base.description || local.description || remote.description) {
    const descRes = mergeLocalizedContent(base.description, local.description, remote.description);
    if (descRes.conflicts.length > 0) {
      conflicts.push(...descRes.conflicts.map((c) => ({ ...c, fieldPath: c.fieldPath.replace('title', 'description') })));
    } else {
      merged.description = descRes.merged;
    }
  }

  // 4. Status Check - Status conflicts never auto-merge
  if (local.status && local.status !== base.status) {
    if (remote.status !== base.status && local.status !== remote.status) {
      conflicts.push({
        fieldPath: 'status',
        baseValue: base.status,
        localValue: local.status,
        remoteValue: remote.status,
        reason: 'Conflicting product status change (e.g. active vs archived)',
      });
    } else {
      merged.status = local.status;
    }
  }

  // 5. Category IDs (Set Union)
  if (local.categoryIds) {
    const baseCats = new Set(base.categoryIds || []);
    const localCats = new Set(local.categoryIds);
    const remoteCats = new Set(remote.categoryIds || []);

    const mergedCats = new Set([...remoteCats]);
    localCats.forEach((c) => {
      if (!baseCats.has(c)) mergedCats.add(c); // User explicitly added category
    });
    baseCats.forEach((c) => {
      if (!localCats.has(c)) mergedCats.delete(c); // User explicitly removed category
    });
    merged.categoryIds = Array.from(mergedCats);
  }

  // 6. Media Attachments Merge
  if (local.media) {
    merged.media = local.media;
  }

  // 7. Schema Extension Merge
  if (local.schemaExtension) {
    if (
      JSON.stringify(local.schemaExtension) !== JSON.stringify(base.schemaExtension) &&
      JSON.stringify(remote.schemaExtension) !== JSON.stringify(base.schemaExtension) &&
      JSON.stringify(local.schemaExtension) !== JSON.stringify(remote.schemaExtension)
    ) {
      conflicts.push({
        fieldPath: 'schemaExtension',
        baseValue: base.schemaExtension,
        localValue: local.schemaExtension,
        remoteValue: remote.schemaExtension,
        reason: 'Conflicting sector schema extension changes',
      });
    } else if (JSON.stringify(local.schemaExtension) !== JSON.stringify(base.schemaExtension)) {
      merged.schemaExtension = local.schemaExtension;
    }
  }

  const isClean = conflicts.length === 0;

  return {
    status: isClean ? 'clean' : 'conflict',
    merged: isClean ? (merged as Product) : undefined,
    conflicts,
    baseRevision,
    localRevision,
    remoteRevision,
  };
}

// ── Ephemeral Draft Storage Helpers ─────────────────────────────────────────

export function getScopedDraftStorageKey(
  workspaceId: string,
  authorId: string,
  productId: string
): string {
  return `plugin:msng3r.commerce:ws:${workspaceId}:user:${authorId}:draft:${productId}`;
}

export function saveEphemeralDraft(
  storage: Storage,
  draft: ListingDraft
): void {
  const key = getScopedDraftStorageKey(draft.workspaceId, draft.authorId, draft.productId);
  storage.setItem(key, JSON.stringify(draft));
}

export function loadEphemeralDraft(
  storage: Storage,
  workspaceId: string,
  authorId: string,
  productId: string
): ListingDraft | null {
  const key = getScopedDraftStorageKey(workspaceId, authorId, productId);
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const draft: ListingDraft = JSON.parse(raw);
    if (new Date(draft.expiresAt).getTime() < Date.now()) {
      storage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function clearEphemeralDraft(
  storage: Storage,
  workspaceId: string,
  authorId: string,
  productId: string
): void {
  const key = getScopedDraftStorageKey(workspaceId, authorId, productId);
  storage.removeItem(key);
}
